import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { PenLine } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { trpc } from "~/lib/trpc";
import { cn } from "~/lib/utils";

interface EditableVariantNameProps {
  variantId: string;
  pageId: string;
  className?: string;
}

export function EditableVariantName({
  variantId,
  pageId,
}: EditableVariantNameProps) {
  const { data } = useSuspenseQuery(
    trpc.variants.getVariantPageData.queryOptions({
      variantId,
      pageId,
    }),
  );

  const currentName = data.variant.name;
  const [savedName, setSavedName] = useState(currentName);
  const [editValue, setEditValue] = useState(currentName);
  const [inputWidth, setInputWidth] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  const queryClient = useQueryClient();

  useEffect(() => {
    setSavedName(currentName);
    setEditValue(currentName);
  }, [currentName]);

  const sharedFontStyles = [
    "cursor-pointer rounded-l",
    "flex items-center gap-1.5",
    "bg-transparent border-none outline-none",
    "focus-within:bg-gray-100/50 focus-within:border focus-within:border-gray-300",
    "w-auto min-w-0",
  ];

  // Measure text width to set input width
  useEffect(() => {
    if (measureRef.current) {
      setInputWidth(measureRef.current.offsetWidth);
    }
  }, [editValue]);

  const updateNameMutation = useMutation(
    trpc.variants.updateName.mutationOptions({
      onSuccess: async () => {
        setSavedName(editValue);
        queryClient.invalidateQueries({
          queryKey: trpc.variants.getVariantPageData.queryOptions({
            variantId,
            pageId,
          }).queryKey,
        });
        toast.success("Variant name updated");
      },
      onError: (error) => {
        toast.error(error.message);
        setEditValue(savedName); // Reset to original value on error
      },
    }),
  );

  const validateName = (name: string): string | null => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.length > 100) {
      return "Name must be less than 100 characters";
    }
    return null;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditValue(savedName);
      requestAnimationFrame(() => {
        inputRef.current?.blur();
      });
    }
  };

  const handleBlur = () => {
    const trimmedValue = editValue.trim();

    if (trimmedValue === savedName.trim()) {
      return;
    }

    const validationError = validateName(trimmedValue);
    if (validationError) {
      toast.error(validationError);
      setEditValue(savedName);
      return;
    }

    updateNameMutation.mutate({
      variantId,
      name: trimmedValue,
    });
  };

  return (
    <div className="inline-flex items-center relative group border border-transparent -ml-2 px-2 py-1 hover:bg-gray-100/50 hover:border-gray-300 rounded focus-within:bg-gray-100/50 focus-within:border-gray-300">
      {/* Hidden span to measure text width */}
      <span
        ref={measureRef}
        className={cn(...sharedFontStyles, "absolute invisible whitespace-pre")}
      >
        {editValue || " "}
      </span>

      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(...sharedFontStyles, "pr-1")}
        style={{ width: `${inputWidth + 16}px` }}
      />
      <div className="absolute right-0 top-0 mx-1 w-3.5 h-full flex items-center justify-center pointer-events-none">
        <PenLine className="size-3.5 text-gray-400 opacity-100 pointer-events-none" />
      </div>
    </div>
  );
}
